/**
 * Playback timing, separated from drawing (TBL-001).
 *
 * The canvas work needs a real browser; this does not. Keeping the clock logic here
 * means the part with actual decisions in it — when a frame is due, when the shot is
 * over, when the final state is adopted — is testable in Node with a fake clock,
 * instead of resting on a screenshot.
 *
 * The clock is the ONLY thing that advances playback. Nothing may look ahead of `tS`
 * (ARCHITECTURE.md §5.1).
 */

export interface Clock {
  now(): number;
  schedule(callback: (nowMs: number) => void): number;
  cancel(handle: number): void;
}

export const browserClock: Clock = {
  now: () => performance.now(),
  schedule: (callback) => requestAnimationFrame(callback),
  cancel: (handle) => {
    cancelAnimationFrame(handle);
  },
};

export interface Playback {
  /** Cancel an in-flight shot; the promise from `start` still settles. */
  stop(): void;
}

/**
 * Drives `onFrame(tS)` until `durationS` has elapsed, then calls `onFinished` exactly
 * once and resolves.
 */
export function playFrames(
  durationS: number,
  clock: Clock,
  onFrame: (tS: number) => void,
  onFinished: () => void,
  /**
   * Slow motion (DBG-001). Wall-clock time is divided by this before it becomes
   * simulated time, so 0.25 plays a shot four times slower. It changes only how fast
   * recorded keyframes are read back — it cannot change what the physics produced.
   */
  timeScale = 1,
): { playback: Playback; done: Promise<void> } {
  const startedMs = clock.now();
  let handle = 0;
  let settled = false;
  let resolveDone: () => void = () => {};

  const done = new Promise<void>((resolve) => {
    resolveDone = resolve;
  });

  const finish = (): void => {
    if (settled) return;
    settled = true;
    onFinished();
    resolveDone();
  };

  const step = (nowMs: number): void => {
    if (settled) return;
    const tS = ((nowMs - startedMs) / 1000) * timeScale;
    onFrame(Math.min(tS, durationS));
    if (tS >= durationS) {
      finish();
      return;
    }
    handle = clock.schedule(step);
  };

  // A zero-length shot still gets one frame, so the board is drawn either way.
  handle = clock.schedule(step);

  return {
    playback: {
      stop() {
        clock.cancel(handle);
        finish();
      },
    },
    done,
  };
}
